import { Content, SECONDARYOPTIONS, StickyMode } from '../ContentType';
import { VtpData } from '../context/ContextTypes';
import { InitialState } from '../FeatureHubAppDefinition';
import { matchI18n } from '../i18n';
import { darkTheme, lightTheme } from '../theme';

export const setHeight = (element: HTMLUListElement | HTMLDivElement, showElement: boolean) => {
  if (!element) return;
  window.requestAnimationFrame(() => {
    element.style.setProperty('--height', showElement ? `${element.scrollHeight}px` : '0px');
  });
};

// Throttles a function to run at most once every `limit` milliseconds
export const throttle = <T extends (...args: unknown[]) => void>(
  func: T,
  limit: number,
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      func(...args);
    }
  };
};

export const getStockData = (initialState: InitialState): VtpData => {
  const { i18nMessages, carlineAvailableStock: stock } = initialState;

  const usedCarsAmount = stock?.usedCars?.amount || 0;
  const newCarsAmount = stock?.newCars?.amount || 0;
  const newCarsUrl = stock?.newCars?.url || '';
  const usedCarsUrl = stock?.usedCars?.url || '';

  const newCarsText = matchI18n('newCars', i18nMessages, {
    value: newCarsAmount.toString(),
  });

  const usedCarsText = matchI18n('usedCars', i18nMessages, {
    value: usedCarsAmount.toString(),
  });

  const vtpLabel = `${matchI18n('available', i18nMessages)} ${stock?.name}` || '';

  return {
    newCarsText,
    usedCarsText,
    usedCarsAmount,
    newCarsAmount,
    newCarsUrl,
    usedCarsUrl,
    vtpLabel,
  };
};

export const getHeadline = (content: Content, initialState: InitialState) => {
  if (
    content.secondaryCta?.secondaryOptions === SECONDARYOPTIONS.VTP &&
    initialState.carlineAvailableStock
  ) {
    return initialState.carlineAvailableStock.name;
  }
  return content.headline;
};

export const shouldRenderCTAs = (content: Content) => {
  return !!(
    (content.cta?.[0]?.text && content.cta[0]?.href) ||
    content.secondaryCta?.secondaryOptions === SECONDARYOPTIONS.CUSTOM ||
    content.secondaryCta?.secondaryOptions === SECONDARYOPTIONS.VTP
  );
};

export const shouldRenderPrimaryButton = (content: Content) => {
  return !!(content.cta?.[0]?.text && content.cta?.[0]?.href);
};

export const shouldRenderVtpButtons = (content: Content, initialState: InitialState) => {
  return !!(
    content.secondaryCta?.secondaryOptions === SECONDARYOPTIONS.VTP &&
    content.secondaryCta?.shopNowCta?.carlineId &&
    initialState.carlineAvailableStock
  );
};

export const shouldRenderSecondaryButton = (content: Content) => {
  return !!(
    content.secondaryCta?.secondaryOptions === SECONDARYOPTIONS.CUSTOM &&
    content.secondaryCta?.customCta?.text &&
    content.secondaryCta?.customCta?.href
  );
};

export const hasStickyMode = (content: Content) => content.stickyMode !== StickyMode.NONE;
export const getTheme = (content: Content) =>
  content.lightDarkMode === 'dark' ? darkTheme : lightTheme;

export const getViewportSize = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width >= 768 && width < 1024) return 'tablet';
  return 'desktop';
};

/**
 * Ensures a given URL string is absolute.
 * If it's relative (e.g. "/my-page"), the function prepends the current domain.
 *
 * @param url - The URL string to check (e.g. "/about" or "https://example.com")
 * @param baseDomain - Optional base domain to use (defaults to window.location.origin in browser)
 * @returns A fully qualified absolute URL string
 */
export function getAbsoluteUrl(url: string, baseDomain?: string): string {
  if (!url) return '';

  try {
    // Check if the URL is already absolute
    const testUrl = new URL(url);
    return testUrl.href; // it's already absolute
  } catch {
    // URL constructor failed => it's relative
    let origin = baseDomain;

    // If no baseDomain provided, use the current origin (browser only)
    if (!origin) {
      if (typeof window !== 'undefined' && window.location.origin) {
        origin = window.location.origin;
      } else {
        throw new Error('Base domain is required when not running in a browser');
      }
    }

    // Normalize slashes to avoid duplicates
    if (url.startsWith('/')) {
      return `${origin}${url}`;
    }
    return `${origin}/${url}`;
  }
}
